import { GetChatStatusFilterCount } from '../components/chat-list-section/chat-row/typings';
import { ChatStatuses, ChatFilterSlaStatus } from '../typings';

type FilterGroupType = GetChatStatusFilterCount['type'];

export const resolveFilterType = (params: {
  slaStatus: ChatFilterSlaStatus;
  assignedUserId: string;
  currentUserId: string | null | undefined;
}): FilterGroupType => {
  const { slaStatus, assignedUserId, currentUserId } = params;

  if (slaStatus === 'breached') return 'SLA_STATUS';
  if (assignedUserId === 'null') return 'UNASSIGNED';
  if (typeof currentUserId === 'string' && currentUserId.length > 0 && currentUserId === assignedUserId)
    return 'MY_CHATS';
  return 'ALL_CHATS';
};

function isAbbreviatedCount(count: string): boolean {
  return isNaN(Number(count));
}

// Parses a count string to its numeric lower bound.
// "1K+" → 1000, "10K+" → 10000, "123" → 123
function parseCountLowerBound(count: string): number {
  if (!isNaN(Number(count))) return Number(count);
  const match = count.match(/^(\d+(?:\.\d+)?)K\+?$/i);
  if (match) return Math.floor(parseFloat(match[1]) * 1000);
  return parseInt(count, 10) || 0;
}

function formatAbbreviatedTotal(n: number): string {
  if (n >= 1000) return `${Math.floor(n / 1000)}K+`;
  return `${n}+`;
}

// Returns the raw display string (e.g. "1K+") for the total count of a given filter/status.
// For ALL status, computes a lower-bound abbreviated total when any sub-count is abbreviated.
export const getRawTotalCountString = (params: {
  filterCounts: GetChatStatusFilterCount[] | null | undefined;
  filterType: FilterGroupType;
  status: ChatStatuses;
}): string | null => {
  const { filterCounts, filterType, status } = params;
  if (!filterCounts) return null;
  const filterGroup = filterCounts.find((fc) => fc.type === filterType);
  if (!filterGroup) return null;

  if (status === ChatStatuses.ALL) {
    const subCounts = filterGroup.counts.filter((c) => c.type !== ChatStatuses.ALL && c.count != null);
    const hasAbbreviated = subCounts.some((c) => isAbbreviatedCount(c.count!));
    if (!hasAbbreviated) return null;
    const lowerBound = subCounts.reduce((sum, c) => sum + parseCountLowerBound(c.count!), 0);
    return formatAbbreviatedTotal(lowerBound);
  }

  const statusCount = filterGroup.counts.find((c) => c.type === status);
  return statusCount?.count ?? null;
};

export const getTotalFilterCount = (params: {
  filterCounts: GetChatStatusFilterCount[] | null | undefined;
  filterType: FilterGroupType;
  status: ChatStatuses;
}): number | null => {
  const { filterCounts, filterType, status } = params;

  if (!filterCounts) return null;

  const filterGroup = filterCounts.find((fc) => fc.type === filterType);
  if (!filterGroup) return null;

  if (status === ChatStatuses.ALL) {
    let total = 0;
    let hasAny = false;
    for (const c of filterGroup.counts) {
      if (c.type !== ChatStatuses.ALL && c.count != null) {
        // Any abbreviated count means we can't give an exact total — caller should use getRawTotalCountString
        if (isAbbreviatedCount(c.count)) return null;
        total += parseInt(c.count, 10);
        hasAny = true;
      }
    }
    return hasAny ? total : null;
  }

  const statusCount = filterGroup.counts.find((c) => c.type === status);
  return statusCount?.count != null ? parseInt(statusCount.count, 10) : null;
};
